import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Info } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AwardReviewItem from '../../components/hr/AwardReviewItem';
import AwardRulesPanel from '../../components/hr/AwardRulesPanel';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import awardService from '../../services/awards';
import type { AwardCode, AwardReference, AwardReviewList, AwardSettings } from '../../types/awards';
import { NOT_ADVICE } from '../../utils/awardReviews';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

/**
 * /hr/award-reviews: every open award pay review reminder in the
 * organisation, soonest first, the people with no award or no date of birth
 * recorded, and the organisation's settings. Timesheet HR seat.
 */

function errText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message || e?.message || fallback;
}

export default function AwardReviews() {
  const { hasAccess, loading } = useModuleLicense('timesheet', 'hr');
  const orgTz = useOrgTimezone();
  const fmt = useCallback((d: string) => formatLocalDateInTz(d, orgTz), [orgTz]);
  const [ref, setRef] = useState<AwardReference | null>(null);
  const [list, setList] = useState<AwardReviewList | null>(null);
  const [settings, setSettings] = useState<AwardSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, l, s] = await Promise.all([awardService.reference(), awardService.reviews(), awardService.settings()]);
      if (r.success && r.data) setRef(r.data);
      if (s.success && s.data) setSettings(s.data);
      if (l.success && l.data) setList(l.data);
      else setError('Could not load the reminders.');
    } catch (err) {
      setError(errText(err, 'Could not load the reminders.'));
    }
  }, []);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const resolve = async (id: number, outcome: 'no_change' | 'dismissed', note: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await awardService.resolve(id, outcome, note);
      if (res.success && res.data) setList(res.data);
      else setError('That did not save. Try again.');
    } catch (err) {
      setError(errText(err, 'That did not save.'));
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await awardService.checkNow();
      if (res.success && res.data) {
        setList(res.data);
        setNotice('Checked. Anything due now is listed.');
      } else setError('Could not check. Try again.');
    } catch (err) {
      setError(errText(err, 'Could not check. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await awardService.saveSettings(settings);
      if (res.success && res.data) {
        setSettings(res.data);
        setNotice('Settings saved.');
        const l = await awardService.reviews();
        if (l.success && l.data) setList(l.data);
      } else setError('That did not save. Try again.');
    } catch (err) {
      setError(errText(err, 'That did not save.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-neutral-500">Loading…</p></div>;
  }
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Award className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR license seat is required for award reviews.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Award reviews"
        subtitle="Birthdays, three months' experience, casual conversion and the wage review: when someone's pay or level may need to change"
        icon={<Award className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 break-words">{error}</div>}
        {notice && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{notice}</div>}

        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-neutral-900">Waiting for a decision</h2>
              <Button size="sm" variant="outline" disabled={busy} onClick={checkNow}>Check now</Button>
            </div>
            {!list ? (
              <p className="text-sm text-neutral-500">Loading…</p>
            ) : list.open.length === 0 ? (
              <p className="text-sm text-neutral-600">Nothing waiting. Reminders appear here, and as a notification to everyone with an HR seat, ahead of the date.</p>
            ) : (
              <ul className="space-y-2">
                {list.open.map(r => (
                  <AwardReviewItem
                    key={r.id}
                    review={r}
                    reference={ref}
                    today={list.today}
                    formatDate={fmt}
                    busy={busy}
                    showPersonLink
                    onResolve={(outcome, note) => resolve(r.id, outcome, note)}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {list && (list.unclassified.length > 0 || list.missing_birth_date.length > 0) && (
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h2 className="text-base font-semibold text-neutral-900">What the reminders cannot see</h2>
              {list.missing_birth_date.length > 0 && (
                <div>
                  <p className="text-sm text-neutral-700">
                    On an award with no date of birth, so the junior rate brackets cannot be checked:
                  </p>
                  <PeopleLinks people={list.missing_birth_date} />
                </div>
              )}
              {list.unclassified.length > 0 && (
                <div>
                  <p className="text-sm text-neutral-700">
                    No award recorded. Not every role is covered by an award; record one for anyone who is.
                  </p>
                  <PeopleLinks people={list.unclassified} />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <AwardRulesPanel reference={ref} defaultAward={settings?.default_award_code ?? null} />

        {settings && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <h2 className="text-base font-semibold text-neutral-900">Settings</h2>
              <div>
                <label htmlFor="award-default" className="block text-xs text-neutral-600 mb-1">Usual award</label>
                <select
                  id="award-default"
                  className="w-full max-w-md border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  value={settings.default_award_code || ''}
                  onChange={e => setSettings(s => s && ({ ...s, default_award_code: (e.target.value || null) as AwardCode | null }))}
                >
                  <option value="">None</option>
                  {ref?.awards.map(a => <option key={a.code} value={a.code}>{a.name} ({a.code})</option>)}
                </select>
                <p className="text-xs text-neutral-500 mt-1 break-words">
                  Offered first when recording someone's level, and used for junior birthdays of anyone with a date of birth but no level yet.
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                  checked={settings.small_business}
                  onChange={e => setSettings(s => s && ({ ...s, small_business: e.target.checked }))}
                />
                <span className="break-words">
                  We are a small business employer (fewer than 15 employees). Casual conversion is counted at {ref?.casual_months_small_business ?? 12} months instead of {ref?.casual_months ?? 6}.
                </span>
              </label>
              <Button size="sm" disabled={busy} onClick={saveSettings}>{busy ? 'Saving…' : 'Save settings'}</Button>
            </CardContent>
          </Card>
        )}

        {list && list.resolved.length > 0 && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <button
                type="button"
                className="text-sm font-medium text-primary-700 hover:underline"
                onClick={() => setShowResolved(v => !v)}
              >
                {showResolved ? 'Hide' : 'Show'} recently dealt with ({list.resolved.length})
              </button>
              {showResolved && (
                <ul className="space-y-2">
                  {list.resolved.map(r => (
                    <AwardReviewItem key={r.id} review={r} reference={ref} today={list.today} formatDate={fmt} showPersonLink />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        <p className="flex items-start gap-2 text-xs text-neutral-500 break-words">
          <Info className="h-4 w-4 shrink-0" />
          <span>{NOT_ADVICE} Only the Horticulture Award 2020 and the Nursery Award 2020 are covered, and no pay rates are kept.</span>
        </p>
      </div>
    </div>
  );
}

function PeopleLinks({ people }: { people: { user_id: number; name: string }[] }) {
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
      {people.map(p => (
        <li key={p.user_id} className="min-w-0">
          <Link to={`/hr/staff?tab=award&user=${p.user_id}`} className="text-sm text-primary-700 hover:underline break-words">
            {p.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}
